import React, { useState } from "react";
import { Card, Button, Modal, Popconfirm, Spin, message } from "antd";
import { SaveOutlined, CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import TablePCMoble from "../data/Table_PC_Moble";
import "../css/popconfirmOkFirst.css";
import {
  search_cc_manual,
  create_cc_manual,
  HEADERS_BASIC90,
} from "../api/apiCent90";

const joinText = (...parts) =>
  parts
    .filter((p) => p !== null && p !== undefined && String(p).trim() !== "")
    .map((p) => String(p).trim())
    .join(" ");

// คอลัมน์ตาม response ของ search-cc-manual (OutputLoadCCH)
const columns = [
  { title: "เลขสัญญา", dataIndex: "CONTNO", key: "CONTNO" },
  { title: "สาขา", dataIndex: "LOCAT", key: "LOCAT" },
  {
    title: "ผู้กู้/คนค้ำประกัน",
    dataIndex: "GARNO",
    key: "GARNO",
    // GARNO เป็น string: "0" = ผู้กู้, "1", "2", ... = คนค้ำลำดับที่
    render: (value) =>
      value === undefined || value === null || value === ""
        ? "-"
        : String(value) === "0"
          ? "ผู้กู้"
          : `คนค้ำ ${value}`,
  },
  {
    title: "ชื่อ-สกุล",
    key: "name",
    render: (value, record) =>
      joinText(record.SNAM, record.NAME1, record.NAME2) || "-",
  },
  {
    title: "ที่อยู่",
    key: "address",
    render: (value, record) =>
      joinText(
        record.ADDR1,
        record.ADDR2,
        record.TUMB,
        record.AUMPDES,
        record.PROVDES,
        record.ZIP,
      ) || "-",
  },
];

/**
 * ModalConfirmLetter
 * ปุ่ม "ยืนยันข้อมูล" เปิด Modal แล้วโหลดข้อมูลสดจาก DB2 ผ่าน search-cc-manual (ไม่เขียน DB)
 * แล้วบันทึกทุกแถวไป /create-cc-manual (field cancelContract) · roundDate = วันที่กดยืนยัน
 *
 * props:
 *  - contno         : เลขสัญญาที่ค้นหาที่หน้า tab
 *  - contractSchema : dataType ที่ใช้ค้นหา
 *  - onSaved        : เรียกหลังบันทึกสำเร็จ (ให้ parent โหลดประวัติใหม่)
 */
function ModalConfirmLetter({ contno, contractSchema, onSaved }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataSearch, setDataSearch] = useState([]); //ตาราง
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const user = localStorage.getItem("USERNAME");
  const company = localStorage.getItem("COMPANY_ID");

  // เปิด Modal แล้วโหลดข้อมูลสดจาก DB2
  const handleOpen = async () => {
    setIsModalOpen(true);
    setDataSearch([]);
    try {
      setLoading(true);
      const res = await axios.post(search_cc_manual, {
        dataType: contractSchema,
        contno,
      });
      console.log("search-cc-manual", res.data);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setDataSearch(data);
      if (data.length === 0) message.warning("ไม่พบเลขสัญญานี้ในบัญชีที่เลือก");
    } catch (err) {
      console.log(err);
      message.error("โหลดข้อมูลสัญญาไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setDataSearch([]);
  };

  // บันทึกสัญญาที่ค้นเจอทั้งหมดเข้าระบบบอกเลิก (create-cc-manual)
  const handleSave = async () => {
    if (dataSearch.length === 0) return;
    try {
      setSaving(true);
      const payload = {
        user,
        company,
        contractSchema,
        roundDate: dayjs().format("YYYY-MM-DD") + "T00:00:00Z", // รอบวันที่ = วันที่กดยืนยัน
        cancelContract: dataSearch,
      };
      const res = await axios.post(create_cc_manual, payload, {
        headers: HEADERS_BASIC90,
      });
      console.log("res.data", res.data);
      const { totalReceived, totalSaved, duplicateCount, blockedCount } =
        res.data || {};
      message.success(
        `บันทึกสำเร็จ ${totalSaved ?? 0}/${totalReceived ?? 0} รายการ` +
          (duplicateCount ? ` · ซ้ำ ${duplicateCount}` : "") +
          (blockedCount ? ` · ถูกบล็อก ${blockedCount}` : ""),
      );
      handleClose();
      if (onSaved) onSaved();
    } catch (err) {
      console.log(err);
      const serverMsg =
        err?.response?.data?.message || err?.response?.data?.error;
      message.error(`บันทึกไม่สำเร็จ${serverMsg ? `: ${serverMsg}` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        color="green"
        variant="solid"
        size="large"
        icon={<CheckCircleOutlined />}
        onClick={handleOpen}
        disabled={!contno}
      >
        บันทึกจดหมายออกมือ
      </Button>

      <Modal
        // วันที่ = roundDate ที่จะบันทึก (วันที่กดยืนยัน)
        title={`บันทึกจดหมายออกมือ · สัญญา ${contno ?? ""} · วันที่ ${dayjs().format("DD/MM/YYYY")}`}
        open={isModalOpen}
        onCancel={handleClose}
        width={"90%"}
        footer={null}
        maskClosable={false}
      >
        <Spin spinning={loading}>
          <Card style={{ marginTop: "12px" }}>
            <TablePCMoble
              ColumnsCase90={columns}
              DataSourceTest={dataSearch}
              // CONTNO ซ้ำกันทุกแถว (ผู้กู้ + คนค้ำ) ต้องใช้ key ที่ API ส่งมา
              rowKey="key"
            />
          </Card>
        </Spin>
        <div style={{ textAlign: "right", marginTop: "16px" }}>
          <Popconfirm
            classNames={{ root: "popconfirm-ok-first" }}
            title="ยืนยันบันทึกข้อมูล"
            description={`บันทึกสัญญา ${contno ?? ""} (${dataSearch.length} รายการ) รอบวันที่ ${dayjs().format("DD/MM/YYYY")} เข้าระบบบอกเลิก?`}
            onConfirm={handleSave}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
            disabled={dataSearch.length === 0 || saving}
          >
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={dataSearch.length === 0}
            >
              บันทึกข้อมูล
            </Button>
          </Popconfirm>
        </div>
      </Modal>
    </>
  );
}

export default ModalConfirmLetter;
